import { createStyles, makeStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NameField } from 'components/NameField';
import { PhoneNumberField } from 'components/PhoneNumberField';
import { RequiredIcon } from 'components/RequiredIcon';
import { TextField } from 'components/TextField';
import { PreambleProps } from 'features/DataCollection/ProgressManager';

import { useStyles as useApplicationFormStyles } from './styles';

export const APPLICATION_FORM_EMAIL_REGEX =
  '^[A-Za-z0-9\\._%+\\-]+[^\\.]@[A-Za-z0-9\\.\\-]+\\.[A-Za-z]{2,}$';

export const useStyles = makeStyles(theme =>
  createStyles({
    columnLayout: {
      gap: 0,
      marginBottom: theme.spacing(4),
    },
    '@global .MuiOutlinedInput-root': {
      marginBottom: theme.spacing(3),
    },
    '@global .MuiFormHelperText-root.Mui-error': {
      marginBottom: theme.spacing(1),
    },
  }),
);

const RequiredLabel: React.VFC<{ text: string }> = ({ text }) => {
  const applicationFormStyles = useApplicationFormStyles();
  return (
    <div className={applicationFormStyles.requiredLabel}>
      <span>{text}</span>
      <RequiredIcon color="error" />
    </div>
  );
};

const formName = (dataKey: string): string => `application_form[${dataKey}]`;

export const applicationFormQuestions =
  (
    countryCode?: string,
    referredByApplicantId?: string,
    referralId?: string,
  ): React.ComponentType<PreambleProps> =>
  ({ showErrors }: PreambleProps) => {
    const applicationFormStyles = useApplicationFormStyles();
    const styles = useStyles();
    const intl = useIntl();

    return (
      <div
        className={cx(applicationFormStyles.columnLayout, styles.columnLayout)}
      >
        <label htmlFor="firstname">
          <RequiredLabel
            text={intl.formatMessage({
              defaultMessage: 'First Name',
              description: 'First Name text field label',
            })}
          />
          <NameField
            id="firstname"
            name={formName('first_name')}
            autoComplete="given-name"
            required
            valueMissingText={
              <FormattedMessage
                defaultMessage="Enter your first name"
                description="Text informing the user that they must enter their first name"
              />
            }
            helperText={
              <FormattedMessage
                defaultMessage="Enter a valid first name"
                description="Text informing the user how to fill out a first name input"
              />
            }
            shouldValidate={showErrors}
          />
        </label>
        <label htmlFor="lastname">
          <RequiredLabel
            text={intl.formatMessage({
              defaultMessage: 'Last Name',
              description: 'Last Name text field label',
            })}
          />
          <NameField
            id="lastname"
            name={formName('last_name')}
            autoComplete="family-name"
            required
            valueMissingText={
              <FormattedMessage
                defaultMessage="Enter your last name"
                description="Text informing the user that they must enter their last name"
              />
            }
            helperText={
              <FormattedMessage
                defaultMessage="Enter a valid last name"
                description="Text informing the user how to fill out a last name input"
              />
            }
            shouldValidate={showErrors}
          />
        </label>
        <input
          type="hidden"
          id="check_if_applicant_is_duplicate"
          name={formName('check_if_applicant_is_duplicate')}
          value="true"
        />
        <label htmlFor="email">
          <RequiredLabel
            text={intl.formatMessage({
              defaultMessage: 'Email',
              description: 'Email text field label',
            })}
          />
          <TextField
            id="email"
            name={formName('email')}
            type="email"
            autoComplete="email"
            required
            pattern={APPLICATION_FORM_EMAIL_REGEX}
            valueMissingText={
              <FormattedMessage
                defaultMessage="Enter a valid email address"
                description="Text informing the user that they must provide an email address"
              />
            }
            helperText={
              <FormattedMessage
                defaultMessage="A valid email is required."
                description="Text informing the user the email address entered does not match the expected format"
              />
            }
            shouldValidate={showErrors}
          />
        </label>
        <label htmlFor="phone">
          <RequiredLabel
            text={intl.formatMessage({
              defaultMessage: 'Phone Number',
              description: 'Phone Number field label',
            })}
          />
          <PhoneNumberField
            id="phone"
            inputNames={{
              phoneNumber: formName('phone_number'),
              countryCode: formName('country_code'),
            }}
            required
            shouldValidate={showErrors}
            countryCode={countryCode}
            valueMissingText={
              <FormattedMessage
                defaultMessage="Enter a valid phone number"
                description="Text informing the user that they must provide an email address"
              />
            }
            helperText={
              <FormattedMessage
                defaultMessage="A valid number is required."
                description="Text informing the user that they must provide a phone number"
              />
            }
          />
        </label>
        <input
          type="hidden"
          id="referred_by_applicant_id"
          name={formName('referred_by_applicant_id')}
          value={referredByApplicantId}
        />
        <input
          type="hidden"
          id="referral_Id"
          name={formName('referral_id')}
          value={referralId}
        />
      </div>
    );
  };
